/**
 * A selectable category facet for the Flight School Finder. `value` is the full tag name the API returns
 * on FlightSchoolResult.tags (so client-side filtering matches against it); `label` is the UI text.
 */
export interface FlightSchoolFacetOption {
  label: string;
  value: string;
}

/**
 * Training Type facets (Flight Training::Classification::*) — OR logic (a school matches if it carries ANY
 * selected tag, e.g. Part 61 OR Part 141), mirroring the iOS app's `orLogicCategories: ["Classification"]`.
 */
export const FLIGHT_SCHOOL_CLASSIFICATION_OPTIONS: FlightSchoolFacetOption[] = [
  { label: 'Part 61', value: 'Flight Training::Classification::Part 61' },
  { label: 'Part 141', value: 'Flight Training::Classification::Part 141' },
  { label: 'Part 142', value: 'Flight Training::Classification::Part 142' },
  { label: 'Part 107', value: 'Flight Training::Classification::Part 107' },
  { label: 'Part 103', value: 'Flight Training::Classification::Part 103' }
];

/**
 * Certifications facets (Flight Training::Certification::*) — AND logic (a school must offer ALL selected).
 */
export const FLIGHT_SCHOOL_CERTIFICATION_OPTIONS: FlightSchoolFacetOption[] = [
  { label: 'Sport Pilot', value: 'Flight Training::Certification::Sport Pilot' },
  { label: 'Recreational Pilot', value: 'Flight Training::Certification::Recreational Pilot' },
  { label: 'Private Pilot', value: 'Flight Training::Certification::Private Pilot' },
  { label: 'Commercial', value: 'Flight Training::Certification::Commercial' },
  { label: 'Airline Transport Pilot ATP', value: 'Flight Training::Certification::Airline Transport Pilot ATP' },
  { label: 'Flight Instructor CFI', value: 'Flight Training::Certification::Flight Instructor CFI' },
  { label: 'Flight Instructor Instrument CFII', value: 'Flight Training::Certification::Flight Instructor Instrument CFII' },
  { label: 'Flight Instructor Sport CFIS', value: 'Flight Training::Certification::Flight Instructor Sport CFIS' },
  { label: 'Multi Engine Instructor MEI', value: 'Flight Training::Certification::Multi Engine Instructor MEI' }
];

/**
 * Ratings facets (Flight Training::Rating::*) — AND logic.
 */
export const FLIGHT_SCHOOL_RATING_OPTIONS: FlightSchoolFacetOption[] = [
  { label: 'Instrument', value: 'Flight Training::Rating::Instrument' },
  { label: 'Multi-Engine', value: 'Flight Training::Rating::Multi-Engine' },
  { label: 'Seaplane', value: 'Flight Training::Rating::Seaplane' },
  { label: 'Glider', value: 'Flight Training::Rating::Glider' },
  { label: 'Rotary Wing', value: 'Flight Training::Rating::Rotary Wing' },
  { label: 'Ultralight', value: 'Flight Training::Rating::Ultralight' }
];

/**
 * Advanced Training facets (Flight Training::Advanced::*) — AND logic.
 */
export const FLIGHT_SCHOOL_ADVANCED_OPTIONS: FlightSchoolFacetOption[] = [
  { label: 'Aerobatic', value: 'Flight Training::Advanced::Aerobatic' },
  { label: 'Aerial Photography', value: 'Flight Training::Advanced::Aerial Photography' },
  { label: 'Air Attack', value: 'Flight Training::Advanced::Air Attack' },
  { label: 'Backcountry Training', value: 'Flight Training::Advanced::Backcountry Training' },
  { label: 'Banner Towing', value: 'Flight Training::Advanced::Banner Towing' },
  { label: 'Cirrus', value: 'Flight Training::Advanced::Cirrus' },
  { label: 'Companion Co-Pilot', value: 'Flight Training::Advanced::Companion Co-Pilot' },
  { label: 'Complex', value: 'Flight Training::Advanced::Complex' },
  { label: 'Drone', value: 'Flight Training::Advanced::Drone' },
  { label: 'Emergency Maneuvers', value: 'Flight Training::Advanced::Emergency Maneuvers' },
  { label: 'Formation', value: 'Flight Training::Advanced::Formation' },
  { label: 'G1000', value: 'Flight Training::Advanced::G1000' },
  { label: 'Glass Cockpit Transition', value: 'Flight Training::Advanced::Glass Cockpit Transition' },
  { label: 'Gyroplane', value: 'Flight Training::Advanced::Gyroplane' },
  { label: 'Helicopter to Fixed-Wing Transition', value: 'Flight Training::Advanced::Helicopter to Fixed-Wing Transition' },
  { label: 'High Altitude', value: 'Flight Training::Advanced::High Altitude' },
  { label: 'High Performance', value: 'Flight Training::Advanced::High Performance' },
  { label: 'Kitfox Type Training', value: 'Flight Training::Advanced::Kitfox Type Training' },
  { label: 'Lighter Than Air', value: 'Flight Training::Advanced::Lighter Than Air' },
  { label: 'Mountain Flying', value: 'Flight Training::Advanced::Mountain Flying' },
  { label: 'Ski Training', value: 'Flight Training::Advanced::Ski Training' },
  { label: 'Spin Training', value: 'Flight Training::Advanced::Spin Training' },
  { label: 'Tailwheel', value: 'Flight Training::Advanced::Tailwheel' },
  { label: 'Turbine', value: 'Flight Training::Advanced::Turbine' },
  { label: 'Upset Recovery', value: 'Flight Training::Advanced::Upset Recovery' }
];

/**
 * Services facets (Flight Training::Services::*) — AND logic.
 */
export const FLIGHT_SCHOOL_SERVICES_OPTIONS: FlightSchoolFacetOption[] = [
  { label: 'Ground School', value: 'Flight Training::Services::Ground School' },
  { label: 'Simulator', value: 'Flight Training::Services::Simulator' },
  { label: 'Intro Flight', value: 'Flight Training::Services::Intro Flight' },
  { label: 'Computerized Testing', value: 'Flight Training::Services::Computerized Testing' },
  { label: 'Examiner', value: 'Flight Training::Services::Examiner' },
  { label: 'Biennial Flight Review', value: 'Flight Training::Services::Biennial Flight Review' },
  { label: 'Instrument Proficiency Check', value: 'Flight Training::Services::Instrument Proficiency Check' },
  { label: 'Annual Recurrent Training', value: 'Flight Training::Services::Annual Recurrent Training' },
  { label: 'Time Building Programs', value: 'Flight Training::Services::Time Building Programs' },
  { label: 'Drone/UAS', value: 'Flight Training::Services::Drone/UAS' },
  { label: 'Part 107 Drone Testing', value: 'Flight Training::Services::Part 107 Drone Testing' },
  { label: 'IFS Certifications', value: 'Flight Training::Services::IFS Certifications' }
];

/**
 * Additional (two-segment) Flight Training facets — AND logic.
 */
export const FLIGHT_SCHOOL_OTHER_OPTIONS: FlightSchoolFacetOption[] = [
  { label: 'VA Approved', value: 'Flight Training::VA Approved' },
  { label: 'College Affiliated', value: 'Flight Training::College Affiliated' }
];

/**
 * Filter state for the Flight School Finder. Holds the keyword plus the selected category facets. Selected
 * facets are stored as arrays of tag names so the OR/AND filtering logic (see
 * SearchPageComponent.filterFlightSchools) can match directly against FlightSchoolResult.tags. Empty arrays
 * mean "no constraint" (show all). Mirrors {@link FlyingClubFinderFilter}.
 */
export class FlightSchoolFinderFilter {

  /** Keyword: school name or airport ident. */
  name = '';

  /** Selected Training Type tag names (OR logic). */
  classification: string[] = [];

  /** Selected Certifications tag names (AND logic). */
  certifications: string[] = [];

  /** Selected Ratings tag names (AND logic). */
  ratings: string[] = [];

  /** Selected Advanced Training tag names (AND logic). */
  advanced: string[] = [];

  /** Selected Services tag names (AND logic). */
  services: string[] = [];

  /** Selected additional (VA Approved / College Affiliated) tag names (AND logic). */
  other: string[] = [];
}
